import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ProjectMemberDraft } from '../../lib/projectMember.types'
import type { TaskTrackerPermissionKey } from '../../lib/taskTracker.permissions.constants'
import { removeProject } from '../projects/projects.slice'
import {
  createEmptyProjectEditProjectState,
  createInitialProjectEditState,
} from './projectEdit.state.utils'
import {
  addProjectMemberThunk,
  fetchProjectEditDataThunk,
  removeProjectMemberThunk,
  saveProjectRolePermissionsThunk,
  saveProjectTitleThunk,
  updateProjectMemberRoleThunk,
} from './projectEdit.thunks'
import { deleteProjectThunk } from '../projects/projects.thunks'

const ensureProject = (
  state: ReturnType<typeof createInitialProjectEditState>,
  projectId: number,
) => {
  if (!state.byProjectId[projectId]) {
    state.byProjectId[projectId] = createEmptyProjectEditProjectState()
  }
  return state.byProjectId[projectId]
}

const projectEditSlice = createSlice({
  name: 'taskTrackerProjectEdit',
  initialState: createInitialProjectEditState(),
  reducers: {
    setProjectEditTitle(
      state,
      action: PayloadAction<{ projectId: number; title: string }>,
    ) {
      ensureProject(state, action.payload.projectId).projectTitle =
        action.payload.title
    },
    toggleProjectEditRolePermission(
      state,
      action: PayloadAction<{
        projectId: number
        roleId: number
        permission: TaskTrackerPermissionKey
      }>,
    ) {
      const editState = ensureProject(state, action.payload.projectId)
      const current = editState.rolePermissionsDraft[action.payload.roleId] ?? []
      const hasPermission = current.includes(action.payload.permission)
      editState.rolePermissionsDraft[action.payload.roleId] = hasPermission
        ? current.filter((item) => item !== action.payload.permission)
        : [...current, action.payload.permission]
    },
    resetProjectEditState(state, action: PayloadAction<number>) {
      state.byProjectId[action.payload] = createEmptyProjectEditProjectState()
    },
    optimisticUpdateProjectMemberRole(
      state,
      action: PayloadAction<{
        projectId: number
        userId: number
        roleId: number
        role: 'admin' | 'employee'
      }>,
    ) {
      const editState = ensureProject(state, action.payload.projectId)
      editState.members = editState.members.map((item) =>
        item.userId === action.payload.userId
          ? {
              ...item,
              roleId: action.payload.roleId,
              role: action.payload.role,
            }
          : item,
      )
    },
    revertProjectEditMembers(
      state,
      action: PayloadAction<{
        projectId: number
        members: ProjectMemberDraft[]
      }>,
    ) {
      ensureProject(state, action.payload.projectId).members =
        action.payload.members
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeProject, (state, action) => {
        delete state.byProjectId[action.payload]
      })
      .addCase(fetchProjectEditDataThunk.pending, (state, action) => {
        const editState = ensureProject(state, action.meta.arg)
        editState.loading = true
        editState.loadError = null
      })
      .addCase(fetchProjectEditDataThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.loading = false
        editState.projectTitle = action.payload.projectTitle
        editState.savedProjectTitle = action.payload.projectTitle
        editState.board = action.payload.board
        editState.members = action.payload.members
        editState.projectRoles = action.payload.projectRoles
        editState.rolePermissionsDraft = action.payload.rolePermissionsDraft
      })
      .addCase(fetchProjectEditDataThunk.rejected, (state, action) => {
        const payload = action.payload as { projectId: number; error: string }
        const editState = ensureProject(state, payload.projectId)
        editState.loading = false
        editState.loadError = payload.error
      })
      .addCase(saveProjectTitleThunk.pending, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).savingProjectTitle =
          true
      })
      .addCase(saveProjectTitleThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.savingProjectTitle = false
        editState.projectTitle = action.payload.projectTitle
        editState.savedProjectTitle = action.payload.projectTitle
        editState.board = action.payload.board
        editState.members = action.payload.members
        editState.projectRoles = action.payload.projectRoles
        editState.rolePermissionsDraft = action.payload.rolePermissionsDraft
      })
      .addCase(saveProjectTitleThunk.rejected, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).savingProjectTitle =
          false
      })
      .addCase(addProjectMemberThunk.pending, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).addingMemberUserId =
          action.meta.arg.userId
      })
      .addCase(addProjectMemberThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.addingMemberUserId = null
        editState.members = [
          ...editState.members.filter(
            (item) => item.userId !== action.payload.member.userId,
          ),
          action.payload.member,
        ]
        if (action.payload.role?.isSystem) {
          const role = action.payload.role
          const exists = editState.projectRoles.some((item) => item.id === role.id)
          if (!exists) {
            editState.projectRoles.push(role)
          }
        }
      })
      .addCase(addProjectMemberThunk.rejected, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).addingMemberUserId =
          null
      })
      .addCase(updateProjectMemberRoleThunk.pending, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).updatingMemberUserId = action.meta.arg.userId
      })
      .addCase(updateProjectMemberRoleThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.updatingMemberUserId = null
        editState.members = editState.members.map((item) =>
          item.userId === action.payload.member.userId
            ? action.payload.member
            : item,
        )
        if (action.payload.role?.isSystem) {
          const role = action.payload.role
          const index = editState.projectRoles.findIndex(
            (item) => item.id === role.id,
          )
          if (index >= 0) {
            editState.projectRoles[index] = role
          } else {
            editState.projectRoles.push(role)
          }
        }
      })
      .addCase(updateProjectMemberRoleThunk.rejected, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).updatingMemberUserId = null
      })
      .addCase(removeProjectMemberThunk.pending, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).removingMemberUserId = action.meta.arg.userId
      })
      .addCase(removeProjectMemberThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.removingMemberUserId = null
        editState.members = editState.members.filter(
          (item) => item.userId !== action.payload.userId,
        )
      })
      .addCase(removeProjectMemberThunk.rejected, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).removingMemberUserId = null
      })
      .addCase(saveProjectRolePermissionsThunk.pending, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).savingRolePermissionsId = action.meta.arg.roleId
      })
      .addCase(saveProjectRolePermissionsThunk.fulfilled, (state, action) => {
        const editState = ensureProject(state, action.payload.projectId)
        editState.savingRolePermissionsId = null
        editState.projectRoles = editState.projectRoles.map((item) =>
          item.id === action.payload.role.id ? action.payload.role : item,
        )
        editState.rolePermissionsDraft[action.payload.role.id] =
          action.payload.permissions
      })
      .addCase(saveProjectRolePermissionsThunk.rejected, (state, action) => {
        ensureProject(
          state,
          action.meta.arg.projectId,
        ).savingRolePermissionsId = null
      })
      .addCase(deleteProjectThunk.pending, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).deletingProject = true
      })
      .addCase(deleteProjectThunk.fulfilled, (state, action) => {
        ensureProject(state, action.payload.projectId).deletingProject =
          false
      })
      .addCase(deleteProjectThunk.rejected, (state, action) => {
        ensureProject(state, action.meta.arg.projectId).deletingProject = false
      })
  },
})

export const {
  setProjectEditTitle,
  toggleProjectEditRolePermission,
  resetProjectEditState,
  optimisticUpdateProjectMemberRole,
  revertProjectEditMembers,
} = projectEditSlice.actions

export default projectEditSlice.reducer
